<template>
  <aside class="relative z-30 hidden lg:flex h-full bg-[#120F14] pt-[80px] transition-all duration-300"
    :class="isMinimized ? 'w-[85px]' : 'w-[300px]'">
    <div
      class="w-full h-full flex flex-col space-y-6 overflow-y-auto max-h-screen custom-scrollbar transition-all duration-300"
      :class="isMinimized ? 'p-3' : 'p-5'">
      <!-- Minimize Button (Top) -->
      <div class="flex w-full pb-3 border-b border-gray-800/40"
        :class="isMinimized ? 'justify-center' : 'justify-between items-center'">
        <span v-if="!isMinimized" class="text-[10px] font-bold text-gray-500 uppercase tracking-widest pl-2">{{ $t('Sidebar.Menu') }}</span>
        <button @click="toggleMinimize"
          class="flex items-center text-[#B9CACB] hover:text-white transition-colors duration-200 gap-2">
          <svg class="w-5 h-5 transition-transform duration-300" :class="isMinimized ? 'rotate-180' : ''" fill="none"
            stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
          <span v-if="!isMinimized" class="text-[10px] font-bold uppercase tracking-wider">{{ $t('Sidebar.Collapse') }}</span>
        </button>
      </div>

      <!-- User Info (only when logged in) -->
      <div v-if="isUserLoggedIn" class="flex items-center w-full pb-2 transition-all duration-300"
        :class="isMinimized ? 'justify-center px-0' : 'gap-3 px-2'">
        <img :src="$store.state.avatar || 'https://i.pravatar.cc/80?img=12'" alt="User Avatar"
          class="h-10 w-10 rounded-full object-cover border-2 border-[#BC13FE] flex-shrink-0" />
        <div v-if="!isMinimized" class="flex flex-col min-w-0">
          <p class="text-white font-bold text-sm tracking-wide overflow-hidden text-ellipsis whitespace-nowrap"
            style="font-family: 'Montserrat', sans-serif">
            {{ $store.state.username || 'Username' }}
          </p>
          <span class="text-xs text-[#BEFF2E] font-medium mt-0.5">{{ $t('Sidebar.Member') }}</span>
        </div>
      </div>

      <nav class="flex flex-col space-y-4">
        <!-- Card 1 -->
        <div class="bg-[#1A1D24] rounded-[12px] flex flex-col overflow-hidden py-1">
          <SidebarItem :minimized="isMinimized" :icon="HomeIcon" :label="$t('Sidebar.Home')" path="/" />
          <SidebarItem :minimized="isMinimized" :icon="PromotionsIcon" :label="$t('Sidebar.Promotions')"
            path="/promotions" />
          <SidebarItem :minimized="isMinimized" :icon="RewardsIcon" :label="$t('Sidebar.Rewards')" path="/rewards" />
        </div>

        <!-- Card 2 -->
        <div class="bg-[#1A1D24] rounded-[12px] flex flex-col overflow-hidden pt-3 pb-2">
          <span v-if="!isMinimized"
            class="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-5 mb-2 mt-1">{{ $t('Sidebar.Gaming') }}</span>
          <SidebarItem :minimized="isMinimized" identifier="leaderboards" :icon="LeaderboardsIcon"
            :label="$t('Sidebar.Leaderboards')" :badge="leaderboardBadge" path="/leaderboards" />
          <SidebarItem :minimized="isMinimized" identifier="store" :icon="StoreIcon" :label="$t('Sidebar.Store')"
            :dropdown="[
              { label: $t('Sidebar.KickStore'), value: 'kick' },
            ]" path="/store" />
          <SidebarItem :minimized="isMinimized" :icon="RafflesIcon" :label="$t('Sidebar.Raffle')" path="/raffle" />
          <SidebarItem :minimized="isMinimized" :icon="GuessTheBalanceIcon" :label="$t('Sidebar.GuessTheBalance')"
            path="/guess-the-balance" />
        </div>

        <!-- Card 3 -->
        <div class="bg-[#1A1D24] rounded-[12px] flex flex-col overflow-hidden pt-3 pb-2">
          <span v-if="!isMinimized"
            class="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-5 mb-2 mt-1">{{ $t('Sidebar.Account') }}</span>
          <SidebarItem :minimized="isMinimized" :icon="SignUpIcon" :label="$t('Sidebar.Sign_Up_Guide')"
            path="/signup-guide" />
          <SidebarItem :minimized="isMinimized" :icon="WagerRewardsIcon" :label="$t('Sidebar.WagerRewards')"
            path="/wager-rewards" />
          <SidebarItem v-if="isUserLoggedIn" :minimized="isMinimized" :icon="ProfileIcon" :label="$t('Sidebar.Profile')" path="/profile" />
          <SidebarItem v-if="isUserLoggedIn" :minimized="isMinimized" :icon="SignOutIcon" :label="$t('Sidebar.Logout')"
            @click="handleLogout" />
        </div>
      </nav>

      <!-- Join Community Button -->
      <div class="mt-auto pt-4 border-t border-gray-800/40 w-full">
        <a href="https://discord.com/invite/N764VWuGyn" target="_blank"
          class="flex items-center justify-center bg-[#BC13FE]/80 hover:bg-[#BC13FE] text-white font-bold transition-all duration-300 rounded-[12px]"
          :class="isMinimized ? 'w-11 h-11 p-0 rounded-[10px] mx-auto' : 'w-full gap-2.5 py-3 px-4'">
          <img src="@/assets/revamp/icons/community.png"
            class="w-auto object-contain brightness-0 invert transition-all duration-300"
            :class="isMinimized ? 'h-3' : 'h-5'" alt="Community" />
          <span v-if="!isMinimized" class="text-sm font-semibold tracking-wide whitespace-nowrap">{{ $t('Sidebar.JoinCommunity') }}</span>
        </a>
      </div>
    </div>
  </aside>
</template>

<script setup>
import SidebarItem from "./SidebarItem.vue";
import HomeIcon from "@/assets/revamp/icons/home.svg";
import RafflesIcon from "@/assets/revamp/icons/raffle.svg";
import GuessTheBalanceIcon from "@/assets/revamp/icons/gtb.svg";
import PromotionsIcon from "@/assets/revamp/icons/promotions.svg";
import RewardsIcon from "@/assets/revamp/icons/rewards.svg";
import SignUpIcon from "@/assets/revamp/icons/sign-up.svg";
import LeaderboardsIcon from "@/assets/revamp/icons/leaderboards.svg";
import ProfileIcon from "@/assets/revamp/icons/profile.svg";
import SignOutIcon from "@/assets/sign-out.svg";
import StoreIcon from "@/assets/revamp/icons/store.svg";
import WagerRewardsIcon from "@/assets/revamp/icons/wager-rewards.svg";
import axios from "axios";
</script>

<script>
export default {
  computed: {
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
  },

  data() {
    const getTotalPrizePoolBadge = () => {
      try {
        const dailyspins = JSON.parse(
          process.env.VUE_APP_DAILYSPINS_LEADERBOARD_CURRENT_PRIZES || "[]"
        );
        const empiredrop = JSON.parse(
          process.env.VUE_APP_EMPIREDROP_LEADERBOARD_CURRENT_PRIZES || "[]"
        );
        const metaspins = JSON.parse(
          process.env.VUE_APP_METASPINS_LEADERBOARD_CURRENT_PRIZES || "[]"
        );
        const raingg = JSON.parse(
          process.env.VUE_APP_RAINGG_LEADERBOARD_CURRENT_PRIZES || "[]"
        );

        const total = [...dailyspins, ...empiredrop, ...metaspins, ...raingg].reduce(
          (acc, val) => acc + val,
          0
        );
        return total >= 1000 ? `${Math.floor(total / 1000)}k` : `${total}`;
      } catch (e) {
        return "";
      }
    };

    return {
      leaderboardBadge: getTotalPrizePoolBadge(),
      isMinimized: localStorage.getItem("sidebar-minimized") === "true",
    };
  },
  methods: {
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      localStorage.setItem("sidebar-minimized", this.isMinimized);
    },
    openLink(site) {
      const urls = {
        dailyspins: "https://dailyspins.com/?ref=BALLYBOY",
        metaspins: "https://metamedialinks.com/e36249d00",
        empiredrop: "https://empiredrop.com/?r=Ballyboy",
        raingg: "https://rain.gg/r/Ballyboy ",
      };

      const url = urls[site];
      if (url) window.open(url, "_blank");
    },
    handleLogout() {
      const token = localStorage.getItem("maherco-access-token");

      if (!token) {
        console.error("No token found, skipping API logout request.");
        return;
      }

      axios
        .post(
          process.env.VUE_APP_BASE_API + "/api/auth/logout",
          {},
          {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then(() => {
          console.log("Successfully logged out from API.");

          // ✅ Clear all Vuex state fields
          this.$store.commit("UPDATE_USER", null);
          this.$store.commit("UPDATE_USERNAME", null);
          this.$store.commit("UPDATE_AVATAR", { avatar: null });
          this.$store.commit("UPDATE_ISVERIFIED", false);

          // ✅ Clear localStorage
          localStorage.removeItem("vuex");
          localStorage.removeItem("maherco-access-token");

          // ✅ Redirect to homepage
          this.$router.replace("/");
        })
        .catch((error) => {
          console.error("Error logging out:", error);
        });
    },
  },
};
</script>
